import { RouteComponentProps } from '@reach/router'
import PageComponent from 'components/Dashboard/PageComponent'
import * as React from 'react'
import { Form, Formik } from 'formik'
import { gql, useMutation } from '@apollo/client'
import { useState } from 'react'
import TextInput from '../TextInput'
import { BulmaMessage } from '../Bulma'
import SubmitButton from '../SubmitButton'
import { DashboardTitle } from './DashboardTitleContext'
import { mutationResponseFragment } from './mutationResponseToToast'
import {
  AnonymizeDataMutation,
  AnonymizeDataMutationVariables,
  AnonymizeDataMutation_anonymizeData,
} from './__generated__/AnonymizeDataMutation'

const anonymizeDataMutation = gql`
  mutation AnonymizeDataMutation($input: AnonymizeDataInput!) {
    anonymizeData(input: $input) {
      kits {
        id
        registrationInfo {
          firstName
          lastName
        }
      }
      orders {
        id
      }

      ...MutationResponseFragment
    }
  }

  ${mutationResponseFragment}
`

export const Anonymize = ({ onSubmit }) => {
  return (
    <>
      <BulmaMessage color="warning">
        <p>Warning: Anonymize is permanent and cannot be reversed</p>
      </BulmaMessage>

      <p className="mb-3 is-bold">Enter the Email Address for the data you wish to Anonymize</p>

      <Formik
        initialValues={{
          emailAddress: '',
          zdRef: '',
          customerReference: '',
        }}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, status }) => (
          <Form>
            <TextInput name="emailAddress" label="Email Address" />
            <TextInput name="zdRef" label="Zendesk ticket no (if applicable)" />
            <TextInput name="customerReference" label="Customer Reference (if applicable)" />

            <div className="buttons my-5">
              <SubmitButton className="is-primary" submitting={isSubmitting} disabled={isSubmitting}>
                Permanently Anonymize Data
              </SubmitButton>
            </div>
          </Form>
        )}
      </Formik>

      <BulmaMessage color="info">
        <p>
          You will receive a report after the Anonymization process that will show the orders and kits that were
          effected
        </p>
      </BulmaMessage>
    </>
  )
}

export const AnonymizeResult = ({ result }) => {
  return (
    <>
      <p className="mb-3 is-bold is-size-5">Anonymize Successful</p>

      <p className="mb-3">The following items have been anonymized:</p>

      <div className="mb-3 is-bold">Kits</div>
      <table className="table">
        <thead>
          <tr>
            <th>Kit Id</th>
            <th>First Name</th>
            <th>Last Name</th>
          </tr>
        </thead>
        <tbody>
          {result.kits.map((kit) => (
            <tr key={kit.id}>
              <td>{kit.id}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mb-3 is-bold">Orders</div>
      <table className="table">
        <thead>
          <tr>
            <th>Order Id</th>
          </tr>
        </thead>
        <tbody>
          {result.orders.map((order) => (
            <tr key={order.id}>
              <td>{order.id}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

export const AnonymizeData: PageComponent = ({}: RouteComponentProps) => {
  const [anonymizeData] = useMutation<AnonymizeDataMutation, AnonymizeDataMutationVariables>(anonymizeDataMutation)
  const [result, setResult] = useState<AnonymizeDataMutation_anonymizeData>()

  const onSubmit = async ({ emailAddress, zdRef, customerReference }) => {
    setResult(undefined)
    const resp = await anonymizeData({
      variables: {
        input: {
          emailAddress,
          zdRef,
          customerReference,
        },
      },
    })

    if (!resp.data.anonymizeData.success) {
      throw new Error(resp.data.anonymizeData.message || 'Something went wrong')
    } else {
      setResult(resp.data.anonymizeData)
    }
  }

  return (
    <div className="container">
      <DashboardTitle title="Anonymize Data" />

      <div className="title mb-3 has-text-centered">Anonymize Data</div>

      {result ? <AnonymizeResult result={result} /> : <Anonymize onSubmit={onSubmit} />}
    </div>
  )
}

AnonymizeData.displayName = 'Anonymize Data'
AnonymizeData.path = '/anonymize-data'

export default AnonymizeData
